export function hasPermission({ permissions, permissionId }) {
  const permission = permissions?.find((p) => p.permissionId === permissionId);
  if (!permission) {
    return false;
  }

  switch (permission.permissionLevel) {
    case 'All':
      return true;
    case 'None':
      return false;
    default:
      return false;
  }
}

/**
 * Check if a permission level value satisfies an exact required level.
 * Uses bitwise AND — true only if that specific bit is set.
 *
 * @param {number} value - The stored bitwise integer for a permission type
 * @param {number} requiredLevel - A PermissionLevelCheck constant
 * @returns {boolean}
 *
 * @example
 * hasPermissionLevel(16, PermissionLevelCheck.all)  // true  (all bit set)
 * hasPermissionLevel(16, PermissionLevelCheck.self) // false (self bit not set)
 */
export const hasPermissionLevel = (value, requiredLevel) =>
  // eslint-disable-next-line no-bitwise
  !!(value & requiredLevel);

/**
 * Check if a permission level meets OR EXCEEDS a minimum threshold.
 * Useful for "self or better" style checks.
 *
 * Priority: none < self < directReports < selfAndDirectReports < all
 *
 * @param {number} value - Stored bitwise integer for a permission type (from permissionsDictionary)
 * @param {number} minimumLevel - The lowest acceptable PermissionLevelCheck value
 * @returns {boolean}
 *
 * @example
 * // User has selfAndDirectReports (8). Does self (2) or better satisfy the check?
 * meetsMinimumPermissionLevel(8, PermissionLevelCheck.self) // true
 * meetsMinimumPermissionLevel(8, PermissionLevelCheck.all)  // false
 */
export const meetsMinimumPermissionLevel = (value, minimumLevel) =>
  value >= minimumLevel;
